import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, CalendarPlus, ListPlus, ChevronDown } from "lucide-react";
import { AppuntamentoModal } from "@/components/AppuntamentoModal";

const iniziali = (f) => (`${(f.nome || f.societa_ragione_sociale || "?")[0] || ""}${(f.cognome || "")[0] || ""}`).toUpperCase();
const URG = { calda: "bg-red-50 text-red-700 border-red-200", media: "bg-amber-50 text-amber-700 border-amber-200", fredda: "bg-sky-50 text-sky-700 border-sky-200" };
const pill = "text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border";

// Fascia in alto della scheda cliente: avatar, nome, contatti cliccabili, etichette.
export function SchedaHeader({ f, nuovo, titoloNuovo }) {
  const nome = [f.nome, f.cognome].filter(Boolean).join(" ") || f.societa_ragione_sociale;
  const tel = (f.telefono || "").replace(/[^\d+]/g, "");
  const wa = tel.replace(/^\+/, "").replace(/^(?!39)(\d{9,10})$/, "39$1");
  return (
    <div className="flex items-center gap-4 min-w-0" data-testid="scheda-header">
      <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full bg-[#FC82ED] text-[#2B2B2B] flex items-center justify-center font-display font-extrabold text-xl" data-testid="scheda-avatar">{nome ? iniziali(f) : "+"}</div>
      <div className="min-w-0">
        <h3 className="font-display text-xl sm:text-2xl font-extrabold truncate" data-testid="scheda-nome">{nome || (nuovo ? titoloNuovo : "Senza nome")}</h3>
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600 mt-0.5">
          {f.telefono && <a href={`tel:${tel}`} data-testid="scheda-chiama" className="inline-flex items-center gap-1 hover:text-[#B8369F]"><Phone size={12} />{f.telefono}</a>}
          {f.telefono && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="scheda-whatsapp" className="inline-flex items-center gap-1 hover:text-emerald-600"><MessageCircle size={12} />WhatsApp</a>}
          {f.email && <a href={`mailto:${f.email}`} data-testid="scheda-scrivi" className="inline-flex items-center gap-1 hover:text-[#B8369F]"><Mail size={12} />{f.email}</a>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {f.urgenza && <span data-testid="scheda-urgenza" className={`${pill} ${URG[f.urgenza] || ""}`}>{f.urgenza}</span>}
          {f.owner_agent_name && <span data-testid="scheda-agente" className={`${pill} bg-slate-50 text-slate-600 border-slate-200`}>Agente: {f.owner_agent_name}</span>}
          <span data-testid="scheda-tipo-soggetto" className={`${pill} bg-[#FC82ED]/10 text-[#B8369F] border-[#FC82ED]/30`}>{f.tipo_soggetto === "giuridica" ? "Persona giuridica" : "Persona fisica"}</span>
        </div>
      </div>
    </div>
  );
}

export function SchedaTabs({ tabs, tab, setTab }) {
  return (
    <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" data-testid="scheda-tabs">
      {tabs.map(([k, l, n]) => (
        <button key={k} type="button" data-testid={`scheda-tab-${k}`} onClick={() => setTab(k)}
          className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-[#2B2B2B] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {l}{n !== undefined && <span className={`ml-1.5 text-[11px] px-1.5 rounded-full ${tab === k ? "bg-[#FC82ED] text-[#2B2B2B]" : "bg-slate-100"}`}>{n}</span>}
        </button>))}
    </div>
  );
}

export const Riquadro = ({ titolo, children, testid }) => (
  <section data-testid={testid} className="rounded-2xl border border-[var(--border)] bg-white p-4 sm:p-5 space-y-3 shadow-[0_1px_8px_rgba(43,43,43,0.04)]">
    {titolo && <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#B8369F]">{titolo}</h4>}
    {children}
  </section>
);

export function AltriDati({ children, testid = "mostra-altri-dati" }) {
  const [open, setOpen] = useState(false);
  return (<>
    <button type="button" data-testid={testid} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#B8369F]">
      <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Nascondi altri dati" : "Mostra altri dati"}
    </button>
    {open && <div className="space-y-3">{children}</div>}
  </>);
}

export const Pillola = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#FC82ED] border-[#FC82ED] text-[#2B2B2B]" : "bg-white border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/60"}`}>{children}</button>
);

function NuovoTaskMini({ nome, cliente, onClose }) {
  const oggi = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
  const [t, setT] = useState({ titolo: `Ricontattare ${nome}`.trim(), scadenza: oggi });
  const [saving, setSaving] = useState(false);
  const salva = async () => {
    if (!t.titolo.trim()) return toast.error("Scrivi il titolo del task");
    setSaving(true);
    try { await api.post("/attivita", { titolo: t.titolo.trim(), scadenza: t.scadenza, priorita: "media", descrizione: `Cliente: ${nome}`, entity_type: cliente.type, entity_id: cliente.id, cliente_id: cliente.id, cliente_tipo: cliente.type }); toast.success("Task creato"); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="absolute bottom-full left-0 mb-2 w-72 rounded-2xl border border-[var(--border)] bg-white shadow-xl p-3 space-y-2 z-10" data-testid="nuovo-task-mini">
      <input data-testid="nuovo-task-mini-titolo" value={t.titolo} onChange={(e) => setT({ ...t, titolo: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-base sm:text-sm" />
      <input data-testid="nuovo-task-mini-scadenza" type="date" value={t.scadenza} onChange={(e) => setT({ ...t, scadenza: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-base sm:text-sm" />
      <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold rounded-lg border">Annulla</button>
        <button data-testid="nuovo-task-mini-salva" disabled={saving} onClick={salva} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#2B2B2B] text-white disabled:opacity-50">Crea task</button></div>
    </div>
  );
}

// Azioni rapide a sinistra della barra in fondo.
export function AzioniRapide({ cliente }) {
  const [app, setApp] = useState(false);
  const [task, setTask] = useState(false);
  if (!cliente?.id) return null;
  return (
    <div className="relative flex items-center gap-1.5 shrink-0">
      <button type="button" data-testid="azione-nuovo-appuntamento" onClick={() => setApp(true)} title="Nuovo appuntamento" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]"><CalendarPlus size={15} /><span className="hidden sm:inline">Nuovo appuntamento</span></button>
      <button type="button" data-testid="azione-nuovo-task" onClick={() => setTask((v) => !v)} title="Nuovo task" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]"><ListPlus size={15} /><span className="hidden sm:inline">Nuovo task</span></button>
      {task && <NuovoTaskMini nome={cliente.label} cliente={cliente} onClose={() => setTask(false)} />}
      {app && <AppuntamentoModal prefill={{ client: cliente, titolo: `Appuntamento ${cliente.label}`.trim() }} onClose={() => setApp(false)} onSaved={() => { setApp(false); toast.success("Appuntamento creato"); }} onDeleted={() => setApp(false)} />}
    </div>
  );
}
